jQuery($ => {
    class GoogleMap {
      constructor(element, args = {}) {
        this._        = element;
        this._element = $(element);
        this._map     = this._element.find('.a-google-map__map');
        this._markers = [];
        this._programmaticChange = false;

        this._storageKeys = LOCAL_STORAGE.user_location.keys;

        this._args = $.extend({
          zoom: 7.5,
          maxZoom: 16,
          mapTypeId: 'roadmap',
          styles: GOOGLE_MAPS.styles,
          scrollwheel: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          fullscreenControlOptions: {
            position: 9
          },
          cameraControl: true,
          cameraControlOptions: {
            position: 3
          }
        }, args);
  
        this.init();
      }
  
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Initialize
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
  
      init() {
        this.setupHandlers();
        this.setupResizeHandler();
        this._element.trigger( 'google-map:init', this );
      }
  
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Setup Handlers
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
  
      setupHandlers() {
        this.setupGoogleMaps();
        this._element.on('google-maps:reset-markers', this.handleResetMarkers);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Setup Google Maps
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      setupGoogleMaps() {
        const lat = parseFloat(this._map.data('lat'));
        const lng = parseFloat(this._map.data('lng'));

        // Read padding from data attributes
        this.setupPaddingFromDataAttributes();

        // Read zoom from data attributes
        const dataZoom = this._map.data('zoom');
        if (dataZoom !== undefined && dataZoom !== null) {
          this._args.zoom = parseFloat(dataZoom);
        }

        // Read mark position configuration
        const markPosition = this._map.data('mark-position');
        if (markPosition === true || markPosition == 'true') {
          this._shouldMarkPosition = true;
        }

        // Track if center was explicitly defined
        let hasExplicitCenter = false;

        // 1) Use data attributes if present
        if (!this._args.center && Number.isFinite(lat) && Number.isFinite(lng)) {
          this._args.center = { lat, lng };
          hasExplicitCenter = true;
        }

        // 2) Otherwise, use local storage if present
        if (!this._args.center) {
          this._args.center = this.checkLocalStorage();
          if (this._args.center) {
            hasExplicitCenter = true;
          }
        }

        // 3) If no center defined, fit markers as initial view
        if (!this._args.center) {
          this._shouldFitMarkersAsInitial = true;
        }

        // Remember initial camera state (may be overridden by fitMarkersInView)
        this._initialCenter = this._args.center;
        this._initialZoom   = this._args.zoom;
        this._anchor        = this._args.center;

        const initMap = () => {
          this._map = new google.maps.Map(this._map[0], this._args);

          google.maps.event.addListenerOnce(this._map, 'idle', () => {
            this._initialZoom = this._map.getZoom();

            // Track drag interaction
            google.maps.event.addListener(this._map, 'dragend', () => {
              this.handleUserInteraction();
            });

            // Track zoom control interaction
            google.maps.event.addListener(this._map, 'zoom_changed', () => {
              if (this._map.getZoom() !== this._initialZoom && !this._programmaticChange) {
                this.handleUserInteraction();
              }
            });

            this._element.trigger('google-map:ready', this._map);
          });

          // Add position marker if configured and center is set
          if (this._shouldMarkPosition && this._initialCenter) {
            google.maps.event.addListenerOnce(this._map, 'projection_changed', () => {
              this.addPositionMarker(this._initialCenter);
            });
          }

          // Apply padding-aware centering if we have an explicit center
          if (hasExplicitCenter && this._paddingConfig) {
            google.maps.event.addListenerOnce(this._map, 'projection_changed', () => {
              const adjustedCenter = this.calculatePaddingAdjustedCenter(
                this._initialCenter,
                this._paddingConfig
              );
              
              if (adjustedCenter) {
                this._map.setCenter(adjustedCenter);
              }
            });
          }

          // Apply any markers that were queued before API was ready
          if (this._pendingMarkers && this._pendingMarkers.length) {
            const pendingFitBounds = this._pendingFitBounds; // Store this
            this.setMarkers(this._pendingMarkers);
            this._pendingMarkers = [];
            
            // If no explicit center was set, use fitMarkersInView as the initial center
            if (this._shouldFitMarkersAsInitial) {
              this.fitMarkersInView(pendingFitBounds || {}, true);
              this._pendingFitBounds = null;
            } else if (pendingFitBounds) {
              // If fitBounds was requested, apply it now and save as initial state
              this.fitMarkersInView(pendingFitBounds, true);
              this._pendingFitBounds = null;
              // Mark that we used fitMarkers as the initial view
              this._usedFitMarkersAsInitial = true;
            }
          }
        };

        if (window.google?.maps?.Map) {
          initMap();
          return;
        }

        this.loadGoogleMapsApi()
          .then(() => initMap())
          .catch(console.error);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Check Local Storage
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      checkLocalStorage = () => {
        const lat = localStorage.getItem(this._storageKeys.lat);
        const lng = localStorage.getItem(this._storageKeys.lng);

        if (!lat || !lng) return null;

        return {
          lat: parseFloat(lat),
          lng: parseFloat(lng)
        };
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Load Google Maps API
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      loadGoogleMapsApi = () => {
        return new Promise((resolve, reject) => {
          if (window.google?.maps?.Map) return resolve();

          const apiKey = GOOGLE_MAPS?.api_key;
          if (!apiKey) return reject(new Error('GOOGLE_MAPS.api_key is missing.'));

          if (window.__googleMapsLoadingPromise) {
            window.__googleMapsLoadingPromise.then(resolve).catch(reject);
            return;
          }

          window.__googleMapsLoadingPromise = new Promise((res, rej) => {
            // If script is already being loaded
            const existingScript = document.querySelector('script[data-google-maps]');
            if (existingScript) {
              existingScript.addEventListener('load', () => res());
              existingScript.addEventListener('error', rej);
              return;
            }

            // Google wants a callback-based init for best practice
            const callbackName = '__initGoogleMaps' + Math.random().toString(16).slice(2);
            window[callbackName] = () => {
              delete window[callbackName];
              res();
            };

            const params = new URLSearchParams({
              key: apiKey,
              v: 'weekly',
              loading: 'async',
              callback: callbackName,
            });

            const script = document.createElement('script');
            script.async = true;
            script.defer = true;
            script.dataset.googleMaps = 'true';
            script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;

            script.onerror = () => {
              delete window[callbackName];
              rej(new Error('Failed to load Google Maps JavaScript API.'));
            };

            document.head.appendChild(script);
          });

          window.__googleMapsLoadingPromise.then(resolve).catch(reject);
        });
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Add Position Marker
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      addPositionMarker = (position) => {
        if (!this._map || !position) return;

        // Remove existing position marker and shadow if present
        if (this._positionMarker) {
          this._positionMarker.setMap(null);
        }
        if (this._positionMarkerShadow) {
          this._positionMarkerShadow.setMap(null);
        }

        const markerOptions = {
          position: position,
          map: this._map,
          zIndex: 1000, // Higher z-index to appear above other markers
        };

        // Use custom 'me' icon if available, otherwise use default marker
        if (GOOGLE_MAPS?.markers?.me) {
          const meConfig = GOOGLE_MAPS.markers.me;
          
          // Check if it's a shape configuration object or an image path
          if (typeof meConfig === 'object' && meConfig.shape) {
            // Shape configuration
            const shapeMap = {
              'circle': google.maps.SymbolPath.CIRCLE,
              'pin': google.maps.SymbolPath.BACKWARD_CLOSED_ARROW
            };
            
            markerOptions.icon = {
              path: shapeMap[meConfig.shape] || google.maps.SymbolPath.CIRCLE,
              scale: meConfig.scale || 8,
              fillColor: meConfig.fillColor || '#4285F4',
              fillOpacity: meConfig.fillOpacity !== undefined ? meConfig.fillOpacity : 1,
              strokeColor: meConfig.strokeColor || '#FFFFFF',
              strokeWeight: meConfig.strokeWeight !== undefined ? meConfig.strokeWeight : 2
            };

            // Add shadow/radius if configured
            if (meConfig.shadowRadius || meConfig.shadowColor) {
              const shadowOptions = {
                position: position,
                map: this._map,
                zIndex: 999, // Behind the main marker
                clickable: false,
                icon: {
                  path: shapeMap[meConfig.shape] || google.maps.SymbolPath.CIRCLE,
                  scale: (meConfig.scale || 8) + (meConfig.shadowRadius || 4),
                  fillColor: meConfig.shadowColor || '#000000',
                  fillOpacity: meConfig.shadowOpacity !== undefined ? meConfig.shadowOpacity : 0.2,
                  strokeWeight: 0
                }
              };
              this._positionMarkerShadow = new google.maps.Marker(shadowOptions);
            }
          } else if (typeof meConfig === 'string') {
            // Image path
            markerOptions.icon = {
              url: meConfig,
              scaledSize: new google.maps.Size(32, 32),
              anchor: new google.maps.Point(16, 16)
            };
          }
        }

        this._positionMarker = new google.maps.Marker(markerOptions);

        google.maps.event.addListener(this._positionMarker, 'click', () => this.handlePositionClick(position));

        return this._positionMarker;
      }
      
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Remove Position Marker
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      removePositionMarker = () => {
        if (this._positionMarker) {
          this._positionMarker.setMap(null);
          this._positionMarker = null;
        }
        if (this._positionMarkerShadow) {
          this._positionMarkerShadow.setMap(null);
          this._positionMarkerShadow = null;
        }
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Handle Position Click
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      handlePositionClick = (position) => {
        this.fitPositionInView(position, {
          zoom: 12,
          padding: this._paddingConfig
        });
        
        this._element.trigger('google-map:position-click', [position]);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Set Position
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      setPosition = (position, options = {}) => {
        if (!position) return;

        this._anchor = position;
        
        // Update or create position marker
        if (this._shouldMarkPosition || options.showMarker) {
          this.addPositionMarker(position);
        }

        // Pan/fit to new position
        if (options.fitInView) {
          this.fitPositionInView(position, options);
        } else {
          this._map.setCenter(position);
        }

        this._element.trigger('google-map:position-changed', position);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Add Marker
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      addMarker = (position, options = {}) => {
        // If API or map not ready, queue this marker to be added later
        if (!this._map || !window.google?.maps?.Marker) {
          if (!this._pendingMarkers) this._pendingMarkers = [];
          this._pendingMarkers.push({ position, ...options });
          return null;
        }

        const markerOptions = Object.assign(
          {
            map      : this._map,
            position : position,
            icon     : GOOGLE_MAPS.markers.default.base,
            cursor   : 'pointer'
          },
          options
        );

        return new google.maps.Marker(markerOptions);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Set Markers
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      setMarkers = (markers = [], options = {}) => {
        if (!this._map) return;

        // Clear existing markers
        if (this._markers) {
          this._markers.forEach(marker => marker.setMap(null));
        }
        this._markers = [];

        if (!Array.isArray(markers) || !markers.length) return;

        markers.forEach((markerConfig) => {
          const { position, ...markerOptions } = markerConfig || {};
          if (!position || typeof position.lat !== 'number' || typeof position.lng !== 'number') {
            return;
          }
          
          const marker = this.addMarker(position, markerOptions);
          if (marker) {
            this.setupMarkerHandlers(marker);
            this._markers.push(marker);
          }
        });

        if (options.fitBounds) {
          if (this._markers.length > 0) {
            this.fitMarkersInView(options.fitBoundsOptions || {});
          } else {
            // Queue for when markers are ready
            this._pendingFitBounds = options.fitBoundsOptions || {};
          }
        }

        this._element.trigger('google-map:markers-set', this._markers);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Setup Marker Handlers
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      setupMarkerHandlers = (marker) => {
        google.maps.event.addListener(marker, 'click', () => this.handleMarkerClick(marker));
        google.maps.event.addListener(marker, 'mouseover', () => this.handleMarkerMouseover(marker));
        google.maps.event.addListener(marker, 'mouseout', () => this.handleMarkerMouseout(marker));
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Handle Marker Click
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      handleMarkerClick = (marker) => {
        this._element.trigger('google-map:marker-click', marker);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Handle Marker Mouseover
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      handleMarkerMouseover = (marker) => {
        this._element.trigger('google-map:marker-mouseover', marker);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Handle Marker Mouseout
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      handleMarkerMouseout = (marker) => {
        this._element.trigger('google-map:marker-mouseout', marker);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Get Markers
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      getMarkers = () => {
        return this._markers;
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Calculate Padding
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      setupPaddingFromDataAttributes = () => {
        const paddingValues = {
          top: this._map.data('padding-top'),
          right: this._map.data('padding-right'),
          bottom: this._map.data('padding-bottom'),
          left: this._map.data('padding-left')
        };

        const padding = Object.entries(paddingValues)
          .filter(([_, value]) => value !== undefined)
          .reduce((result, [side, value]) => {
            result[side] = isNaN(value) ? value : parseFloat(value);
            return result;
          }, {});

        if (Object.keys(padding).length > 0) {
          this._args.padding = padding;
        }

        // Store original padding config for responsive updates
        this._paddingConfig = this._args.padding;
      }

      calculatePaddingAdjustedCenter = (center, paddingConfig) => {
        if (!this._map || !center || !paddingConfig) return null;

        const projection = this._map.getProjection();
        if (!projection) return null;

        const padding = this.calculatePadding(paddingConfig);
        
        // Check if any padding is actually set (not all zeros)
        const hasMeaningfulPadding = Object.values(padding).some(value => value !== 0);
        
        if (!hasMeaningfulPadding) return null;
        
        // Get the map's container dimensions
        const mapDiv = this._map.getDiv();
        const mapWidth = mapDiv.offsetWidth;
        const mapHeight = mapDiv.offsetHeight;
        
        // Calculate the center of the visible (non-padded) area in pixels
        // from the top-left of the map container
        const visibleCenterX = padding.left + (mapWidth - padding.left - padding.right) / 2;
        const visibleCenterY = padding.top + (mapHeight - padding.top - padding.bottom) / 2;
        
        // Calculate the actual center of the full map container
        const mapCenterX = mapWidth / 2;
        const mapCenterY = mapHeight / 2;
        
        // Calculate the pixel offset needed
        const offsetX = mapCenterX - visibleCenterX;
        const offsetY = mapCenterY - visibleCenterY;
        
        if (offsetX === 0 && offsetY === 0) return null;
        
        // Convert the center point to a pixel coordinate
        const centerPixel = projection.fromLatLngToPoint(center);
        const scale = Math.pow(2, this._map.getZoom());
        
        // Apply the offset in world coordinates
        const newCenterPixel = new google.maps.Point(
          centerPixel.x + (offsetX / scale),
          centerPixel.y + (offsetY / scale)
        );
        
        // Convert back to lat/lng
        return projection.fromPointToLatLng(newCenterPixel);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Calculate Padding
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      calculatePadding = (padding) => {
        // If padding is a number, apply to all sides
        if (typeof padding === 'number') {
          return { top: padding, right: padding, bottom: padding, left: padding };
        }

        // Process each side of padding object
        const result = { top: 0, right: 0, bottom: 0, left: 0 };
        let hasSelectors = false;
        const sides = ['top', 'right', 'bottom', 'left'];

        sides.forEach(side => {
          const value = padding[side];

          if (value === undefined) {
            result[side] = 0;
          } else if (typeof value === 'number') {
            // Direct pixel value
            result[side] = value;
          } else if (typeof value === 'string') {
            // jQuery selector - get element dimensions
            hasSelectors = true;

            const $el = $(value);

            if ($el.length) {
              if (side === 'top' || side === 'bottom') {
                result[side] = $el.outerHeight() || 0;
              } else {
                result[side] = $el.outerWidth() || 0;
              }
            }
          } else if (value instanceof jQuery) {
            // jQuery object directly
            hasSelectors = true;

            if (side === 'top' || side === 'bottom') {
              result[side] = value.outerHeight() || 0;
            } else {
              result[side] = value.outerWidth() || 0;
            }
          }
        });

        // Store if we have selectors for responsive handling
        if (hasSelectors && padding === this._paddingConfig) {
          this._hasResponsivePadding = true;
        }

        return result;
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Update Padding
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      updatePadding = (padding) => {
        if (!this._map) return;

        this._paddingConfig = padding;
        this._args.padding = padding;

        // Refit the current view with new padding
        if (this._markers?.length > 0) {
          this.fitMarkersInView({ padding: padding });
        } else if (this._anchor) {
          this.fitPositionInView(this._anchor, {
            zoom: this._map.getZoom(),
            padding: padding
          });
        }

        this._element.trigger('google-map:padding-updated', padding);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Update Zoom
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      updateZoom = (zoom) => {
        if (!this._map) return;

        this._programmaticChange = true;
        this._map.setZoom(zoom);
        this._args.zoom = zoom;

        this._element.trigger('google-map:zoom-updated', zoom);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Setup Resize Handler
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      setupResizeHandler = () => {
        let resizeTimeout;
        
        $(window).on('resize', () => {
          // Only handle resize if we have selector-based padding
          if (!this._hasResponsivePadding || !this._map) return;

          // Debounce resize events
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            this.handleResponsivePaddingUpdate();
          }, 250);
        });
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Handle Responsive Padding Update
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      handleResponsivePaddingUpdate = () => {
        const newPadding = this.calculatePadding(this._paddingConfig);
        const oldPadding = this._currentPadding || {};
        
        // Check if padding values changed
        const sides = ['top', 'right', 'bottom', 'left'];
        const hasChanged = sides.some(side => newPadding[side] !== oldPadding[side]);

        if (!hasChanged) return;

        this._currentPadding = newPadding;

        this._element.trigger('google-map:padding-changed', newPadding);

        // Maintain current view when padding changes
        if (this._markers?.length > 0) {
          const currentCenter = this._map.getCenter();
          if (currentCenter) {
            this._map.setCenter(currentCenter);
          }
        }
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Fit Markers In View
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      fitMarkersInView = (options = {}, saveAsInitial = false) => {
        if (!this._map || !this._markers?.length) return;

        const bounds = new google.maps.LatLngBounds();

        if (options.position) {
          bounds.extend(options.position);
        }
        
        this._markers.forEach(marker => {
          const position = marker.getPosition();
          if (position) {
            bounds.extend(position);
          }
        });

        const paddingValue = options.padding !== undefined ? options.padding : this._args.padding;
        
        if (paddingValue) {
          const padding = this.calculatePadding(paddingValue);
          this._map.fitBounds(bounds, padding);
        } else {
          this._map.fitBounds(bounds);
        }

        if (saveAsInitial) {
          google.maps.event.addListenerOnce(this._map, 'idle', () => {
            this._initialCenter = this._map.getCenter();
            this._initialZoom   = this._map.getZoom();
            this._anchor        = this._initialCenter;
          });
        }

        // Limit zoom on single marker to avoid excessive close-up
        if (options.maxZoom && this._markers.length === 1) {
          google.maps.event.addListenerOnce(this._map, 'idle', () => {
            if (this._map.getZoom() > options.maxZoom) {
              this._map.setZoom(options.maxZoom);
            }
          });
        }
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Fit Single Marker In View
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      fitMarkerInView = (marker, options = {}) => {
        if (!this._map || !marker) return;

        const position = marker.getPosition();
        if (!position) return;

        this.fitPositionInView(position, options);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Fit Position In View
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      fitPositionInView = (position, options = {}) => {
        if (!this._map || !position) return;

        this._programmaticChange = true; 

        const zoom = options.zoom || 12;
        this._map.setZoom(zoom);

        const paddingValue = options.padding !== undefined ? options.padding : this._args.padding;
        
        if (paddingValue) {
          const adjustedCenter = this.calculatePaddingAdjustedCenter(position, paddingValue);
          this._map.setCenter(adjustedCenter || position);
        } else {
          this._map.setCenter(position);
        }

        this._anchor = position;

        setTimeout(() => {
          this._programmaticChange = false;
        }, 100);
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Reset Position
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      resetPosition = () => {
        if (!this._map) return;

        const wasInitializedWithFitMarkers = this._shouldFitMarkersAsInitial || this._usedFitMarkersAsInitial;
        
        if (wasInitializedWithFitMarkers && this._markers?.length > 0) {
          this.fitMarkersInView({ padding: this._paddingConfig });
        }
        else if (this._paddingConfig && this._initialCenter) {
          this._anchor = this._initialCenter;
          this.fitPositionInView(this._initialCenter, {
            zoom: this._initialZoom,
            padding: this._paddingConfig
          });
        }
        else if (this._initialCenter) {
          this._anchor = this._initialCenter;
          if (Number.isFinite(this._initialZoom)) {
            this._map.setZoom(this._initialZoom);
            this._map.setCenter(this._initialCenter);
          } else {
            this._map.setCenter(this._initialCenter);
          }
        }

        this._element.trigger('google-map:reset-position');
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Handle User Interaction
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      handleUserInteraction = () => {
        this._element.trigger('google-map:user-interaction');
      }

      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
      // ✅ Update Args
      //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

      updateArgs = (newArgs) => {
        if (!this._map) return;

        // Merge new args with existing args
        this._args = $.extend(this._args, newArgs);

        // Apply specific updates based on what changed
        if (newArgs.zoom !== undefined) {
          this._programmaticChange = true;
          this._map.setZoom(newArgs.zoom);
        }

        if (newArgs.padding !== undefined) {
          this._paddingConfig = newArgs.padding;
          
          // Refit the current view with new padding
          if (this._markers?.length > 0) {
            this.fitMarkersInView({ padding: newArgs.padding });
          } else if (this._anchor) {
            this.fitPositionInView(this._anchor, {
              zoom: this._map.getZoom(),
              padding: newArgs.padding
            });
          }
        }

        if (newArgs.scrollwheel !== undefined) {
          this._map.setOptions({ scrollwheel: newArgs.scrollwheel });
        }

        if (newArgs.styles !== undefined) {
          this._map.setOptions({ styles: newArgs.styles });
        }

        if (newArgs.maxZoom !== undefined) {
          this._map.setOptions({ maxZoom: newArgs.maxZoom });
        }

        // Apply any other map options that can be updated
        const mapOptions = {};
        const updatableOptions = [
          'mapTypeId', 'mapTypeControl', 'streetViewControl', 
          'fullscreenControl', 'fullscreenControlOptions',
          'cameraControl', 'cameraControlOptions'
        ];

        updatableOptions.forEach(option => {
          if (newArgs[option] !== undefined) {
            mapOptions[option] = newArgs[option];
          }
        });

        if (Object.keys(mapOptions).length > 0) {
          this._map.setOptions(mapOptions);
        }

        this._element.trigger('google-map:args-updated', this._args);
      }
    }
  
    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
    // ✅ Setup jQuery Plugin
    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
  
    $.fn.google_map = function() {
      var _ = this,
          opt = arguments[0],
          args = Array.prototype.slice.call(arguments, 1),
          l = _.length,
          i,
          ret;
      for (i = 0; i < l; i++) {
        if (typeof opt == 'object' || typeof opt == 'undefined')
          _[i].google_map = new GoogleMap(_[i], opt);
        else
          ret = _[i].google_map[opt].apply(_[i].google_map, args);
        if (typeof ret != 'undefined') return ret;
      }
      return _;
    }
  
    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
    // ✅ Run
    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
  
    $('.a-google-map').google_map();
});